import JSURL from 'jsurl';

import {ChartType, ReportName} from 'common/report/constants';
import {ReportHeader, ReportHeaderJsxProps} from 'common/report/report-manager/report-header';
import {InsightsBookmarkParams, InsightsQueryResults} from 'common/report/insights/util/types';
import {
  getDisplayOptions,
  getResultsKeysAndValues,
  hasMoreThanOneTimeValue,
  hasValidMetricBookmarkParams,
} from 'common/report/insights/util';
import {SearchDisplayContext} from 'common/report/util/search/types';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {HeaderCustomAlertParams} from 'common/query-builder/query-report-header/types';
import {Section, TimeSection} from 'common/report/insights/models/section';
import {isValidAnomalyDetectionBookmark} from 'common/widgets/alerts/custom-alert/utils';

import {Report} from '../models/report';
import {getDateRange} from '../util/time';

export class InsightsReportHeader extends ReportHeader<InsightsBookmarkParams, InsightsQueryResults> {
  displayContext = SearchDisplayContext.Insights;
  reportType = ReportName.Insights;
  customAlertsEnabled = true;
  csvExportEnabled = true;

  public reportHeaderCustomAlertParams(): HeaderCustomAlertParams {
    const displayOptions = getDisplayOptions(this.bookmark.params.sections, this.bookmark.params.displayOptions);
    const chartType = displayOptions.chartType;
    const timeUnit = this.bookmark.params.sections.time[0].unit ?? TimeUnit.Day;
    const timeSection = Section.deserialize(TimeSection.TYPE, this.bookmark.params.sections.time);
    const dateRange = getDateRange(timeSection.clauses);

    return {
      isCustomAlertsActionVisible: true,
      resultsKeysAndValues: getResultsKeysAndValues(this.bookmark.params, this.results, this.dataGroups),
      customAlertIntervalDays: dateRange.getIntervalDays(),
      customAlertTimeUnit: timeUnit,
      enableCustomAlertConditionTypes: chartType === ChartType.Line && hasMoreThanOneTimeValue(this.results),
      enableAnomalyDetectionControls: isValidAnomalyDetectionBookmark(this.bookmark.type, this.bookmark.params),
      bookmarkParams: this.bookmark.params,
    };
  }

  getInvalidParamsMessage(): string {
    if (!hasValidMetricBookmarkParams(this.bookmark.params)) {
      return `Please select at least one event to save your insights report`;
    }

    return null;
  }

  getStandaloneHash(titleOverride?: string) {
    const report = Report.fromBookmarkData(this.bookmark.params);
    if (titleOverride) {
      report.title = titleOverride;
    }

    const stringifiedReport = JSURL.stringify(report.toUrlData());
    if (this.bookmark.id && !titleOverride) {
      return `report/${this.bookmark.id}/${stringifiedReport}`;
    }

    return stringifiedReport;
  }
}

export const InsightsReportHeaderEl = defineAndMakeJsxFactory<ReportHeaderJsxProps>(
  `insights-report-header`,
  InsightsReportHeader,
);
export const InsightsReportHeaderReact = panel2react<ReportHeaderJsxProps>(
  `insights-report-header`,
  InsightsReportHeader,
);
